import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, Shield, ShieldCheck, ShieldOff, Eye, EyeOff, Copy, Check } from 'lucide-react'
import { useAuthStore } from '../../stores/auth.store'
import { useSetup2FA, useConfirm2FA } from '../../modules/auth/hooks/useAuth'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/axios'
import { notificationsApi } from '../../modules/notifications/api/notifications.api'

const pwdSchema = z
  .object({
    currentPassword: z.string().min(1, 'Requis'),
    newPassword: z.string().min(8, 'Minimum 8 caractères'),
    confirm: z.string(),
  })
  .refine((d) => d.newPassword === d.confirm, {
    message: 'Ne correspond pas',
    path: ['confirm'],
  })

export default function ProfilePage() {
  const { user } = useAuthStore()
  const [showQR, setShowQR] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  const { mutate: setup2fa, data: setup2faData, isPending: setupPending } = useSetup2FA()
  const { mutate: confirm2fa, isPending: confirmPending, data: confirmed } = useConfirm2FA()

  const {
    mutate: changePwd,
    isPending: pwdPending,
    isSuccess: pwdSuccess,
  } = useMutation({
    mutationFn: (dto: any) => api.post('/users/me/change-password', dto),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(pwdSchema),
  })

  const [totpCode, setTotpCode] = useState('')

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-[var(--tx)] mb-1">Mon profil</h1>
        <p className="text-[var(--tx3)] text-sm">
          Gérez vos informations personnelles et la sécurité de votre compte.
        </p>
      </div>

      {/* Infos profil */}
      <section className="bg-white border border-[var(--bd)] rounded-2xl p-6">
        <h2 className="text-[var(--tx)] font-semibold mb-4">Informations</h2>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Prénom', value: user?.firstName },
            { label: 'Nom', value: user?.lastName },
            { label: 'Email', value: user?.email },
            { label: 'Rôle', value: user?.roles?.[0] },
          ].map(({ label, value }) => (
            <div key={label}>
              <div className="text-[var(--tx3)] text-xs mb-1">{label}</div>
              <div className="text-[var(--tx)] text-sm font-medium">{value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Changement mot de passe */}
      <section className="bg-white border border-[var(--bd)] rounded-2xl p-6">
        <h2 className="text-[var(--tx)] font-semibold mb-4">Changer le mot de passe</h2>
        <form
          onSubmit={handleSubmit((d) => {
            changePwd(d)
            reset()
          })}
          className="space-y-4"
        >
          {(['currentPassword', 'newPassword', 'confirm'] as const).map((field) => (
            <div key={field}>
              <label className="block text-[var(--tx3)] text-xs mb-1.5">
                {field === 'currentPassword'
                  ? 'Mot de passe actuel'
                  : field === 'newPassword'
                    ? 'Nouveau mot de passe'
                    : 'Confirmer'}
              </label>
              <div className="relative">
                <input
                  {...register(field)}
                  type={showPwd ? 'text' : 'password'}
                  className="w-full bg-[var(--surface-2)] border border-[var(--bd)] text-[var(--tx)] text-sm
                             rounded-xl px-4 py-2.5 pr-10 focus:outline-none focus:border-blue-500
                             focus:ring-1 focus:ring-[var(--blue)] transition-colors"
                />
                {field === 'newPassword' && (
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tx3)]"
                  >
                    {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
              {errors[field] && (
                <p className="text-[var(--red-text)] text-xs mt-1">{errors[field]?.message as string}</p>
              )}
            </div>
          ))}

          {pwdSuccess && (
            <p className="text-[var(--green-text)] text-sm">Mot de passe modifié. Reconnectez-vous.</p>
          )}

          <button
            type="submit"
            disabled={pwdPending}
            className="bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium
                       rounded-xl px-5 py-2.5 transition-colors flex items-center gap-2"
          >
            {pwdPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Modifier le mot de passe
          </button>
        </form>
      </section>

      {/* 2FA */}
      <section className="bg-white border border-[var(--bd)] rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          {user?.twoFactorEnabled ? (
            <ShieldCheck className="w-5 h-5 text-[var(--green-text)]" />
          ) : (
            <Shield className="w-5 h-5 text-[var(--tx3)]" />
          )}
          <h2 className="text-[var(--tx)] font-semibold">Authentification à deux facteurs</h2>
          <span
            className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
              user?.twoFactorEnabled
                ? 'bg-[var(--green-bg)] border border-[rgba(16,185,129,0.25)] text-[var(--green-text)]'
                : 'bg-white border border-[var(--bd)] text-[var(--tx3)]'
            }`}
          >
            {user?.twoFactorEnabled ? 'Activé' : 'Désactivé'}
          </span>
        </div>

        {!user?.twoFactorEnabled && !setup2faData && (
          <div>
            <p className="text-[var(--tx3)] text-sm mb-4">
              Renforcez la sécurité de votre compte avec une application authenticator (Google
              Authenticator, Authy).
            </p>
            <button
              onClick={() => setup2fa()}
              disabled={setupPending}
              className="bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm font-medium
                         rounded-xl px-5 py-2.5 transition-colors flex items-center gap-2"
            >
              {setupPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Configurer le 2FA
            </button>
          </div>
        )}

        {setup2faData && !confirmed && (
          <div className="space-y-4">
            <p className="text-[var(--tx3)] text-sm">
              Scannez ce QR code avec votre application authenticator :
            </p>
            <div className="flex justify-center bg-white rounded-xl p-4 w-fit">
              <img src={setup2faData.qrCode} alt="QR Code 2FA" className="w-40 h-40" />
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-[var(--surface-2)] text-[var(--tx2)] text-xs px-3 py-2 rounded-lg font-mono">
                {setup2faData.secret}
              </code>
              <button
                onClick={() => copyToClipboard(setup2faData.secret, 'secret')}
                className="p-2 rounded-lg hover:bg-[var(--surface-2)] text-[var(--tx3)]"
              >
                {copied === 'secret' ? (
                  <Check className="w-4 h-4 text-[var(--green-text)]" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Code à 6 chiffres"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                maxLength={6}
                className="flex-1 bg-[var(--surface-2)] border border-[var(--bd)] text-[var(--tx)] text-sm
                           rounded-xl px-4 py-2.5 focus:outline-none focus:border-blue-500 transition-colors"
              />
              <button
                onClick={() => confirm2fa(totpCode)}
                disabled={confirmPending || totpCode.length !== 6}
                className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-[var(--tx)] text-sm font-medium
                           rounded-xl px-5 py-2.5 transition-colors flex items-center gap-2"
              >
                {confirmPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmer
              </button>
            </div>
          </div>
        )}

        {confirmed && (
          <div className="space-y-3">
            <p className="text-[var(--green-text)] text-sm font-medium">✓ 2FA activé avec succès !</p>
            <div className="bg-[var(--amber-bg)] border border-[rgba(245,158,11,0.25)] rounded-xl p-4">
              <p className="text-[var(--amber-text)] text-sm font-medium mb-2">
                ⚠ Codes de récupération — copiez-les maintenant
              </p>
              <p className="text-[var(--amber-text)]/70 text-xs mb-3">Ces codes ne seront plus affichés.</p>
              <div className="grid grid-cols-2 gap-1.5">
                {confirmed.recoveryCodes.map((code, i) => (
                  <code
                    key={i}
                    className="text-xs bg-[var(--surface-2)] px-2 py-1 rounded font-mono text-[var(--tx2)]"
                  >
                    {code}
                  </code>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Sprint 3 — Préférences de notifications */}
      <NotificationPreferencesSection />
    </div>
  )
}

function NotificationPreferencesSection() {
  const { data: prefs, isLoading } = useQuery({
    queryKey: ['notif-preferences'],
    queryFn: () => notificationsApi.getPreferences(),
  })
  const queryClient = useQueryClient()

  const updateMut = useMutation({
    mutationFn: (dto: any) => notificationsApi.updatePreferences(dto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notif-preferences'] }),
  })

  if (isLoading || !prefs) return null

  const Toggle = ({ label, checked, field }: { label: string; checked: boolean; field: string }) => (
    <label className="flex items-center justify-between py-2">
      <span className="text-sm text-[var(--tx)]">{label}</span>
      <button
        onClick={() => updateMut.mutate({ [field]: !checked })}
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-[var(--blue)]' : 'bg-[var(--bd)]'}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </label>
  )

  return (
    <section className="bg-white border border-[var(--bd)] rounded-2xl p-6">
      <h2 className="text-lg font-bold text-[var(--tx)] mb-4">Préférences de notifications</h2>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-2">Canaux</h3>
          <div className="space-y-1 border-b border-[var(--bd)] pb-3">
            <Toggle label="Notifications in-app" checked={prefs.inApp} field="inApp" />
            <Toggle label="Notifications par email" checked={prefs.email} field="email" />
            <Toggle label="SMS (urgences uniquement)" checked={prefs.sms} field="sms" />
          </div>

          <h3 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mt-4 mb-2">Fréquence email</h3>
          <select
            value={prefs.emailDigest ?? 'immediate'}
            onChange={e => updateMut.mutate({ emailDigest: e.target.value })}
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm"
          >
            <option value="immediate">Immédiat</option>
            <option value="daily">Résumé quotidien (18h)</option>
            <option value="weekly">Résumé hebdomadaire</option>
          </select>
        </div>

        <div>
          <h3 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-2">Types de notifications</h3>
          <div className="space-y-1">
            <Toggle label="Tâche assignée" checked={prefs.taskAssigned} field="taskAssigned" />
            <Toggle label="Échéance tâche" checked={prefs.taskDue} field="taskDue" />
            <Toggle label="Tâche en retard" checked={prefs.taskOverdue} field="taskOverdue" />
            <Toggle label="Statut congés" checked={prefs.leaveStatus} field="leaveStatus" />
            <Toggle label="Alerte budget" checked={prefs.budgetAlert} field="budgetAlert" />
            <Toggle label="Alerte stock" checked={prefs.stockAlert} field="stockAlert" />
            <Toggle label="Alerte géofence" checked={prefs.geofenceAlert} field="geofenceAlert" />
            <Toggle label="Workflow factures" checked={prefs.invoiceWorkflow} field="invoiceWorkflow" />
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-[var(--bd)]">
        <h3 className="text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-2">Heures calmes (pas de SMS/email)</h3>
        <div className="flex items-center gap-3">
          <input
            type="time" value={prefs.quietHoursStart ?? '20:00'}
            onChange={e => updateMut.mutate({ quietHoursStart: e.target.value })}
            className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm"
          />
          <span className="text-sm text-[var(--tx3)]">à</span>
          <input
            type="time" value={prefs.quietHoursEnd ?? '07:00'}
            onChange={e => updateMut.mutate({ quietHoursEnd: e.target.value })}
            className="px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm"
          />
        </div>
      </div>
    </section>
  )
}
